import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useFunnel } from '../context/FunnelContext'
import { useAbandonedCartTracking } from '../hooks/useAbandonedCartTracking'
import { submitToTellescope } from '../utils/tellescope'
import { isSyncVisit } from '../utils/visitTypeLogic'
import { logger } from '../utils/logger'
import { 
  trackPaymentSuccessfulConversion, 
  trackPaymentSuccessfulAsyncConversion 

} from '../utils/everflowConversions'
import { logPaymentStatus } from '../utils/funnelLogger'

// Import funnel configs
// import funnel2Config from '../../funnels/funnel2/config.json'
import funnel3Config from '../../funnels/funnel3/config.json'
// import funnelT1Config from '../../funnels/funnel-t1/config.json'

import './PaymentReturn.scss'

const PaymentReturn = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { goToStep, getFunnelConfig, setFunnelConfig, setActiveFunnel, isInitialized, funnelData, updateFunnelData } = useFunnel()
  const { clearTimer } = useAbandonedCartTracking(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const sessionId = searchParams.get('session_id')
  
  // Map funnel tracking IDs to config files
  const getFunnelConfigFile = (funnelId) => {
    const configMap = {
      // 'wellness': funnel2Config,
      'md': funnel3Config,
      // 'md-t1': funnelT1Config
    }
    return configMap[funnelId] || funnel3Config
  }
  
  // Map funnel tracking IDs to routes
  const getFunnelRoute = (funnelId) => {
    const routeMap = {
      'wellness': '/funnel2',
      'md': '/funnel3',
      'md-t1': '/funnel/t1'
    }
    const route = routeMap[funnelId] || '/funnel3'
    logger.debug('getFunnelRoute - funnelId:', funnelId, '-> route:', route)
    return route
  }
  
  // Use the tracking funnel_id directly (same as FunnelRouter)
  const FUNNEL_ID = funnelData.funnel_id
  
  // Debug logging
  logger.debug('PaymentReturn - FUNNEL_ID:', FUNNEL_ID)
  logger.debug('PaymentReturn - Target route:', FUNNEL_ID ? getFunnelRoute(FUNNEL_ID) : 'N/A')

  // Initialize funnel config if needed
  useEffect(() => {
    if (!isInitialized || !FUNNEL_ID) {
      return
    }

    const existingConfig = getFunnelConfig(FUNNEL_ID)
    if (!existingConfig) {
      const configFile = getFunnelConfigFile(FUNNEL_ID)
      setActiveFunnel(FUNNEL_ID)
      setFunnelConfig(FUNNEL_ID, configFile)
    }
  }, [isInitialized, FUNNEL_ID, getFunnelConfig, setFunnelConfig, setActiveFunnel])

  useEffect(() => {
    // Wait for initialization before proceeding
    if (!isInitialized) {
      return
    }

    if (!FUNNEL_ID) {
      setError('No active funnel found. Please start from the beginning.')
      setLoading(false)
      setTimeout(() => navigate('/'), 2000)
      return
    }

    // Wait for funnel config to be loaded
    const funnelConfig = getFunnelConfig(FUNNEL_ID)
    if (!funnelConfig) {
      return // Wait for config to be loaded by the first useEffect
    }

    if (!sessionId) {
      setError('No session ID provided')
      setLoading(false)
      return
    }

    // Get session status from backend
    const checkSessionStatus = async () => {
      try {
        const formData = new URLSearchParams()
        formData.append('session_id', sessionId)

        const response = await fetch('/get-session-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString()
        })

        if (!response.ok) {
          throw new Error('Failed to get session status')
        }

        const data = await response.json()

        if (data.success && data.data) {
          const sessionStatus = data.data.status || data.data.payment_status

          // If payment is successful, navigate to payment_successful step
          if (sessionStatus === 'complete' || sessionStatus === 'paid' || sessionStatus === 'succeeded') {
            
            // Clear abandoned cart timer
            clearTimer()
            logger.debug('[Abandoned Cart] Payment completed - timer cleared')
            
            // Extract order data from session
            const orderData = {
              amount: data.data.amount_total ? (data.data.amount_total / 100) : 0, // Stripe amount is in cents
              customer_id: data.data.customer || data.data.id || sessionId,
              display_order_total: data.data.amount_total ? (data.data.amount_total / 100) : 0
            }

            // Update payment status to successful
            updateFunnelData({ payment_status: 'successful' })

            // Log payment status
            logPaymentStatus(funnelData.entry_id, 'successful', {
              email: funnelData.email,
              amount: orderData.amount,
              customer_id: orderData.customer_id,
              session_id: sessionId,
              funnel_id: funnelData.funnel_id,
              promo_code: funnelData.promo_code || null,
              promo_id: funnelData.promo_id || null,
              promo_percent_off: funnelData.promo_percent_off || null,
              promo_amount_off: funnelData.promo_amount_off || null
            })

            // Submit finalized data to Tellescope
            submitToTellescope(funnelData, true).catch(err => logger.error('Tellescope submit error:', err))

            // Determine if sync or async visit
            const isSync = isSyncVisit(funnelData)
            const targetStepId = isSync ? 'payment_successful' : 'payment_successful_async'
            
            // Track Everflow payment conversion
            if (isSync) {
              trackPaymentSuccessfulConversion(funnelData, orderData)
            } else {
              trackPaymentSuccessfulAsyncConversion(funnelData, orderData)
            }

            const funnelConfig = getFunnelConfig(FUNNEL_ID)
            if (funnelConfig && funnelConfig.steps) {
              const paymentSuccessStepIndex = funnelConfig.steps.findIndex(
                (s) => s.id === targetStepId
              )
              if (paymentSuccessStepIndex !== -1) {
                logger.debug('Payment Success - Navigating to step:', paymentSuccessStepIndex, 'of funnel:', FUNNEL_ID)
                // Ensure active funnel is set before navigation
                setActiveFunnel(FUNNEL_ID)
                goToStep(FUNNEL_ID, paymentSuccessStepIndex)
                // Use setTimeout to ensure state updates are persisted before navigation
                setTimeout(() => {
                  const targetRoute = getFunnelRoute(FUNNEL_ID)
                  logger.debug('Payment Success - Navigating to route:', targetRoute)
                  navigate(targetRoute)
                }, 100)
              } else {
                setError(`${targetStepId} step not found`)
                setLoading(false)
              }
            } else {
              setError('Funnel configuration not found')
              setLoading(false)
            }
          } else {
            // Payment failed or incomplete, go back to checkout with error
            // Log payment failure
            logPaymentStatus(funnelData.entry_id, 'failed', {
              email: funnelData.email,
              session_id: sessionId,
              session_status: sessionStatus,
              funnel_id: funnelData.funnel_id,
              promo_code: funnelData.promo_code || null,
              promo_id: funnelData.promo_id || null,
              promo_percent_off: funnelData.promo_percent_off || null,
              promo_amount_off: funnelData.promo_amount_off || null
            })

            const funnelConfig = getFunnelConfig(FUNNEL_ID)
            if (funnelConfig && funnelConfig.steps) {
              const checkoutStepIndex = funnelConfig.steps.findIndex(
                (s) => s.id === 'bmi_checkout_page'
              )
              if (checkoutStepIndex !== -1) {
                // Ensure active funnel is set before navigation
                setActiveFunnel(FUNNEL_ID)
                goToStep(FUNNEL_ID, checkoutStepIndex)
                // Use setTimeout to ensure state updates are persisted before navigation
                setTimeout(() => {
                  navigate(`${getFunnelRoute(FUNNEL_ID)}?error=${encodeURIComponent('Payment was not successful. Please try again.')}`)
                }, 100)
              } else {
                setError('Checkout page not found')
                setLoading(false)
              }
            } else {
              setError('Funnel configuration not found')
              setLoading(false)
            }
          }
        } else {
          throw new Error(data.data?.message || 'Failed to verify payment status')
        }
      } catch (err) {
        logger.error('Error checking session status:', err)
        setError(err.message || 'An error occurred while verifying payment')
        setLoading(false)

        // Log payment error
        logPaymentStatus(funnelData.entry_id, 'failed', {
          email: funnelData.email,
          session_id: sessionId,
          error: err.message,
          funnel_id: funnelData.funnel_id,
          promo_code: funnelData.promo_code || null,
          promo_id: funnelData.promo_id || null,
          promo_percent_off: funnelData.promo_percent_off || null,
          promo_amount_off: funnelData.promo_amount_off || null
        })

        // On error, redirect back to checkout
        setTimeout(() => {
          const funnelConfig = getFunnelConfig(FUNNEL_ID)
          if (funnelConfig && funnelConfig.steps) {
            const checkoutStepIndex = funnelConfig.steps.findIndex(
              (s) => s.id === 'bmi_checkout_page'
            )
            if (checkoutStepIndex !== -1) {
              // Ensure active funnel is set before navigation
              setActiveFunnel(FUNNEL_ID)
              goToStep(FUNNEL_ID, checkoutStepIndex)
              setTimeout(() => {
                navigate(`${getFunnelRoute(FUNNEL_ID)}?error=${encodeURIComponent(err.message || 'Payment verification failed')}`)
              }, 100)
            }
          }
        }, 2000)
      }
    }

    checkSessionStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isInitialized, FUNNEL_ID, getFunnelConfig(FUNNEL_ID)])

  if (loading) {
    return (
      <div className="payment-return">
        <div className="payment-return-container">
          <div className="loading-spinner">Verifying payment...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="payment-return">
        <div className="payment-return-container">
          <div className="error-message">{error}</div>
          <p>Redirecting...</p>
        </div>
      </div>
    )
  }

  return null
}

export default PaymentReturn
